<template>
  <span class="span-preview">
    <span
      ref="trigger"
      class="span-preview__trigger"
      role="button"
      tabindex="0"
      @mouseenter="onEnter"
      @mouseleave="onLeave"
      @click.stop="onTriggerClick"
      @keydown.enter.prevent="onTriggerClick"
      @keydown.space.prevent="onTriggerClick"
    >
      <slot name="trigger" />
    </span>
    <teleport to="body">
      <div
        v-if="open"
        ref="popover"
        class="span-preview__popover"
        :style="popoverStyle"
        @mouseenter="onEnter"
        @mouseleave="onLeave"
        @click.stop
      >
        <p
          v-if="periodLabel"
          class="span-preview__period"
        >
          {{ periodLabel }}
        </p>
        <slot />
      </div>
    </teleport>
  </span>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'YhcServiceLevelSpanPreview',
  props: {
    periodLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      hovering: false,
      pinned: false,
      placed: false,
      top: 0,
      left: 0,
      closeTimer: 0,
      suppressHoverUntil: 0,
      listening: false,
    }
  },
  computed: {
    open(): boolean {
      return this.hovering || this.pinned
    },
    popoverStyle(): Record<string, string> {
      return {
        top: `${this.top}px`,
        left: `${this.left}px`,
        visibility: this.placed ? 'visible' : 'hidden',
      }
    },
  },
  watch: {
    open(value: boolean) {
      if (value) {
        this.placed = false
        this.$nextTick(() => {
          this.place()
          requestAnimationFrame(() => this.place())
        })
        this.attach()
        return
      }
      this.detach()
    },
  },
  beforeUnmount() {
    window.clearTimeout(this.closeTimer)
    this.detach()
  },
  methods: {
    onEnter() {
      if (Date.now() < this.suppressHoverUntil) return
      window.clearTimeout(this.closeTimer)
      this.hovering = true
    },
    onLeave() {
      window.clearTimeout(this.closeTimer)
      this.closeTimer = window.setTimeout(() => {
        if (!this.pinned) this.hovering = false
      }, 180)
    },
    onTriggerClick() {
      this.pinned = !this.pinned
      if (!this.pinned) {
        this.hovering = false
        this.suppressHoverUntil = Date.now() + 400
      }
    },
    onDocClick(event: MouseEvent) {
      const target = event.target as Node | null
      const trigger = this.$refs.trigger as HTMLElement | undefined
      const popover = this.$refs.popover as HTMLElement | undefined
      if (target && trigger?.contains(target)) return
      if (target && popover?.contains(target)) return
      this.pinned = false
      this.hovering = false
    },
    onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        this.pinned = false
        this.hovering = false
      }
    },
    place() {
      const trigger = this.$refs.trigger as HTMLElement | undefined
      const popover = this.$refs.popover as HTMLElement | undefined
      if (!trigger || !popover) return
      const rect = trigger.getBoundingClientRect()
      const pop = popover.getBoundingClientRect()
      const margin = 8
      const gap = 8
      let top = rect.top - pop.height - gap
      if (top < margin) top = rect.bottom + gap
      if (top + pop.height > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - margin - pop.height)
      }
      let left = rect.left
      if (left + pop.width > window.innerWidth - margin) {
        left = window.innerWidth - margin - pop.width
      }
      if (left < margin) left = margin
      this.top = top
      this.left = left
      this.placed = true
    },
    attach() {
      if (this.listening) return
      this.listening = true
      window.addEventListener('resize', this.place)
      window.addEventListener('scroll', this.place, true)
      document.addEventListener('keydown', this.onKey)
      document.addEventListener('click', this.onDocClick)
    },
    detach() {
      if (!this.listening) return
      this.listening = false
      window.removeEventListener('resize', this.place)
      window.removeEventListener('scroll', this.place, true)
      document.removeEventListener('keydown', this.onKey)
      document.removeEventListener('click', this.onDocClick)
    },
  },
})
</script>

<style>
.span-preview {
  display: inline;
}

.span-preview__trigger {
  cursor: help;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}

.span-preview__popover {
  position: fixed;
  z-index: 10000;
  box-sizing: border-box;
  min-width: min(22rem, calc(100vw - 16px));
  max-width: calc(100vw - 16px);
  padding: 0.75rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  text-align: left;
}

.span-preview__period {
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #dc3545;
  line-height: 1.3;
}

.span-preview__popover .time-band-group,
.span-preview__popover .working-days-group {
  margin-bottom: 0;
}

.span-preview-fallback {
  margin: 0;
  font-size: 0.875rem;
  color: #dc3545;
}
</style>
